/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useEffect, useRef } from 'react'
import { Alert, Box, CircularProgress, Divider, Grid, Typography, useTheme } from '@mui/material'
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { ButtonType } from '../../../components/GenericButton/styles'
import FormInput from '../../../components/FormElements/FormInput'
import FormSwitch from '../../../components/FormElements/FormSwitch'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { notification } from '../../../components/Notifications'
import { type ISlot } from '../../../models/DistributionPlan'
import { type AxiosResponse, type AxiosError } from 'axios'
import { getSlotSectionApi } from '../../../store/slices/distributionPlanSlice/apis'
import { useAppDispatch } from '../../../store'
import { useParams } from 'react-router-dom'
import { surveyRulesSchema } from './constant'
import { type ICourseFilters } from '../../SurveyTempleteFeature/components/FiltersModal'
import AttributesFilter from '../../SurveyTempleteFeature/components/FiltersModal/AttributesFilter'
import AutoImportManager from '../../../APIManager/AutoImportManager'
import MultiSelectApiAutoComplete from '../../../components/FormElements/MultiSelectApiAutoComplete'
import GenericAutocomplete from '../../../components/FormElements/FormAutoComplete'
import { AcademicUnitTypeAndUnitRow, ACADEMIC_UNIT_COMBINED_HELPER } from '../../../components/AcademicUnitTypeAndUnitRow'
import { useAcademicUnitTypesQuery, type AcademicUnitTypeOption } from '../../../hooks/useAcademicUnitTypesQuery'
import type { ISyncRules } from '../../../models/SyncRules'
import type { IAttribute } from '../../../store/slices/SurveyTemplateSlice'
import { useSlotTaskContext } from '../../../contexts/SlotTaskContext'
import { isInstructorReleaseOnGradePostingEnabled } from '../../../consts/messages'

interface SyncRulesPanelProps {
  handleCloseModal: () => void
  slot: ISlot
  isLatestDataFetching: boolean
  latestSyncRules: ISyncRules | null | undefined
  syncRulesData: ISyncRules | undefined
  loading: boolean
  isReadOnly: boolean
}

interface FormType {
  isSyncAllSectionsForTermActive?: boolean
  isReportingHierarchyFilterActive?: boolean
  isCourseAttributeFilterActive?: boolean
  /** `category_name` for scoped `/api/divisions/` search when reporting hierarchy filter is on */
  reportingHierarchyCategoryName?: string | null
  academicUnits?: Array<{ divisionId: string }>
  courseFilters?: ICourseFilters[]
  minimumEnrollment?: number | null
}

const SyncRulesPanel: React.FC<SyncRulesPanelProps> = ({
  handleCloseModal, slot, isLatestDataFetching, latestSyncRules, syncRulesData, loading, isReadOnly
}) => {
  const { id } = useParams()
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { setTask, checkIfBlocked } = useSlotTaskContext()
  const [attributes, setAttributes] = React.useState<IAttribute[]>([])
  const queryClient = useQueryClient()
  const methods = useForm<any>({ resolver: yupResolver(surveyRulesSchema), defaultValues: {} })

  const { handleSubmit, control, watch, reset, setValue, clearErrors, formState: { isDirty } } = methods

  const { mutate, isPending: isPendingAutoImport } = useMutation({
    mutationFn: AutoImportManager.submitAutoImport,
    onSuccess: (res: AxiosResponse) => {
      if (res?.status === 202) {
        const { taskId, message } = res.data ?? {}
        if (taskId) {
          setTask(taskId, message ?? 'Auto sync started', 'Course sections synced successfully.')
        }
        notification(message ?? 'Auto sync started', {
          variant: 'success',
          subTitle: 'Course sections are updating in the background using the sync rules you submitted.'
        })
        void dispatch(getSlotSectionApi({ urlId: id }))
        void queryClient.invalidateQueries({ queryKey: ['slotDataById', id] })
        void queryClient.invalidateQueries({ queryKey: ['syncRulesData', slot.slotId] })
        handleCloseModal()
        return
      }
      notification(res?.data?.status ?? 'Successfully Updated', { variant: 'success' })
      void dispatch(getSlotSectionApi({ urlId: id }))
      void queryClient.invalidateQueries({ queryKey: ['slotDataById', id] })
      void queryClient.invalidateQueries({ queryKey: ['syncRulesData', slot.slotId] })
    },
    onError: (error: AxiosError) => {
      const errorMessage = error.message
      let messageToShow = 'Bad request. Please check the data and try again'
      if (typeof errorMessage === 'string') {
        messageToShow = errorMessage
      } else if (errorMessage && typeof errorMessage === 'object') {
        const firstKey = Object.keys(errorMessage)[0]
        if (firstKey) {
          messageToShow = errorMessage[firstKey]
        }
      }

      notification(messageToShow, { variant: 'error' })
    }
  })

  const onSubmit: SubmitHandler<FormType> = async (values) => {
    if (isReadOnly) {
      return
    }

    if (await checkIfBlocked()) return

    const { isSyncAllSectionsForTermActive, isCourseAttributeFilterActive, isReportingHierarchyFilterActive, minimumEnrollment, academicUnits, courseFilters } = values
    if (isCourseAttributeFilterActive && courseFilters?.length === 0) {
      notification('At least one Course Attributes filter is required', { variant: 'error' })
      return
    }

    const preparedAttributes = courseFilters?.map(({ op, value, key }: ICourseFilters) => ({
      op,
      values: value,
      type: key?.attributeType,
      key: key?.attributeKey
    }))

    const payload = {
      isSyncAllSectionsForTermActive,
      isCourseAttributeFilterActive: isSyncAllSectionsForTermActive ? false : isCourseAttributeFilterActive,
      isReportingHierarchyFilterActive: isSyncAllSectionsForTermActive ? false : isReportingHierarchyFilterActive,
      minimumEnrollment,
      academicUnits: isReportingHierarchyFilterActive && !isSyncAllSectionsForTermActive ? academicUnits?.map(({ divisionId }) => divisionId) : undefined,
      attributes: isCourseAttributeFilterActive && !isSyncAllSectionsForTermActive ? preparedAttributes : undefined
    }

    mutate({ slotId: slot.slotId, data: payload })
  }

  useEffect(() => {
    if (syncRulesData) {
      reset({
        isSyncAllSectionsForTermActive: syncRulesData.isSyncAllSectionsForTermActive ?? false,
        isReportingHierarchyFilterActive: syncRulesData.isReportingHierarchyFilterActive ?? false,
        isCourseAttributeFilterActive: syncRulesData.isCourseAttributeFilterActive ?? false,
        minimumEnrollment: syncRulesData.minimumEnrollment ?? 0,
        academicUnits: syncRulesData?.academicUnits ?? [],
        reportingHierarchyCategoryName: null
      })
      setAttributes(syncRulesData?.attributes ?? [])
    }
  }, [syncRulesData, reset])

  const handleLatestSurveyRulesClicked = (): void => {
    if (latestSyncRules) {
      setValue('isSyncAllSectionsForTermActive', latestSyncRules.isSyncAllSectionsForTermActive ?? false, { shouldDirty: true })
      setValue('isReportingHierarchyFilterActive', latestSyncRules.isReportingHierarchyFilterActive ?? false, { shouldDirty: true })
      setValue('isCourseAttributeFilterActive', latestSyncRules.isCourseAttributeFilterActive ?? false, { shouldDirty: true })
      setValue('minimumEnrollment', latestSyncRules.minimumEnrollment ?? 0, { shouldDirty: true })
      setValue('academicUnits', latestSyncRules?.academicUnits ?? [], { shouldDirty: true })
      setValue('reportingHierarchyCategoryName', null, { shouldDirty: true })

      if (latestSyncRules.isCourseAttributeFilterActive && latestSyncRules?.attributes?.length > 0) {
        setAttributes(latestSyncRules.attributes)
      }
      notification('Latest project sync rules settings applied.', { variant: 'success' })
    }
  }
  const isReportingHierarchyFilterActive = watch('isReportingHierarchyFilterActive')
  const isCourseAttributeFilterActive = watch('isCourseAttributeFilterActive')
  const isSyncAllSectionsForTermActive = watch('isSyncAllSectionsForTermActive')
  const reportingHierarchyCategoryName = watch('reportingHierarchyCategoryName')
  const isGradePostingReleaseEnabled = isInstructorReleaseOnGradePostingEnabled(slot.isInstructorReleaseOnGradePostingEnabled)
  const { data: academicUnitTypes = [], isLoading: academicTypesLoading } = useAcademicUnitTypesQuery()
  const prevHierarchyCategoryRef = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (!isReportingHierarchyFilterActive || isSyncAllSectionsForTermActive) return
    const cat = reportingHierarchyCategoryName ?? ''
    if (prevHierarchyCategoryRef.current !== undefined && prevHierarchyCategoryRef.current !== cat) {
      setValue('academicUnits', [], { shouldDirty: true, shouldValidate: false })
      clearErrors('academicUnits')
    }
    prevHierarchyCategoryRef.current = cat
  }, [isReportingHierarchyFilterActive, isSyncAllSectionsForTermActive, reportingHierarchyCategoryName, clearErrors, setValue])

  useEffect(() => {
    if (!isCourseAttributeFilterActive) {
      setValue('courseFilters', [])
    }
  }, [isCourseAttributeFilterActive, setValue])

  // Read-only is driven by topbar status/permission rules.
  const isButtonDisabled = isReadOnly
  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>

          {
            loading

              ? <Box display="flex" justifyContent="center" alignItems="center" minHeight={theme.spacing(100)}>
                <CircularProgress />
              </Box>

              : <>
                <FormSwitch
                  name="isSyncAllSectionsForTermActive"
                  control={control}
                  label="Sync All Course Sections from Term"
                  checked={isSyncAllSectionsForTermActive}
                  disabled={isButtonDisabled}
                />
                <Typography color="#000" sx={{ fontSize: '14px', marginTop: 2 }}>
                  Sync all Course Sections that fall under the linked Term.
                </Typography>
                {isGradePostingReleaseEnabled && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Auto-synced sections arrive with instructor release unset unless the Survey Dates
                    instructor-release rule is enabled.
                  </Alert>
                )}

                <Divider sx={{ mt: 4, mb: 5 }} />
                <FormSwitch
                  name="isReportingHierarchyFilterActive"
                  control={control}
                  label="Reporting Hierarchy"
                  disabled={isSyncAllSectionsForTermActive || isButtonDisabled }
                  checked={isReportingHierarchyFilterActive && !isSyncAllSectionsForTermActive}
                />
                <Typography color="#000" sx={{ fontSize: '14px', marginTop: 2 }}>
                  Sync Course Sections from this term that fall under the following Academic Units.
                </Typography>

                {isReportingHierarchyFilterActive && !isSyncAllSectionsForTermActive && (
                  <Grid item mt={3} xs={12}>
                    <AcademicUnitTypeAndUnitRow
                      groupLabel="Academic Unit"
                      helperText={ACADEMIC_UNIT_COMBINED_HELPER}
                      groupRequired={isReportingHierarchyFilterActive}
                      typeControl={
                        <GenericAutocomplete<AcademicUnitTypeOption>
                          name="reportingHierarchyCategoryName"
                          placeholder="Select type"
                          options={academicUnitTypes}
                          getOptionLabel={(o) => o.categoryName}
                          getOptionValue={(o) => o.categoryName}
                          loading={academicTypesLoading}
                          readOnly={isButtonDisabled}
                          clearValue=""
                        />
                      }
                      unitControl={
                        <MultiSelectApiAutoComplete
                          apiEndpoint="/api/divisions/"
                          placeholder="Search for academic units"
                          name="academicUnits"
                          getOptionValue={(option: any) => option.divisionId ?? ''}
                          getOptionLabel={(option: any) => option.title ?? ''}
                          disabled={isButtonDisabled}
                          required={isReportingHierarchyFilterActive}
                          skip={!reportingHierarchyCategoryName}
                          sortField="title"
                          apiExtraParams={reportingHierarchyCategoryName
                            ? { categoryName: reportingHierarchyCategoryName, scope: 'my' }
                            : undefined}
                        />
                      }
                    />
                  </Grid>
                )}

                <Divider sx={{ mt: 4, mb: 5 }} />
                <FormSwitch
                  name="isCourseAttributeFilterActive"
                  control={control}
                  label="Course Attributes"
                  disabled={isSyncAllSectionsForTermActive || isButtonDisabled}
                  checked={isCourseAttributeFilterActive && !isSyncAllSectionsForTermActive}
                />
                <Typography color="#000" sx={{ fontSize: '14px', marginTop: 2 }}>
                  Sync Course Sections from this term that have the following Attributes.
                </Typography>

                {isCourseAttributeFilterActive && !isSyncAllSectionsForTermActive && (
                  <AttributesFilter attributes={attributes ?? []} initialLabel="Sync if" />
                )}

                <Divider sx={{ mt: 4, mb: 5 }} />
                <Box>
                  <Typography color="#000" sx={{ fontWeight: 600, fontSize: '16px' }}>
                    Minimum Course Enrollment
                  </Typography>
                  <Typography color="#000" sx={{ fontSize: '14px', marginTop: 2 }}>
                    Minimum enrollment required for a course section survey to be available.
                  </Typography>
                  <Box mt={4}>
                    <FormInput disabled={isButtonDisabled} name="minimumEnrollment" placeholder="Set Minimum Enrollment"/>
                  </Box>
                </Box>
              </>
          }
          <DefaultModalActions
            confirmButtonProps={{
              type: 'submit',
              text: 'Submit',
              buttonType: ButtonType.Primary,
              disabled: !isDirty || isPendingAutoImport || isButtonDisabled,
              loading: isPendingAutoImport
            }}
            destructiveButtonProps={{
              onClick: handleCloseModal
            }}
            prevSettingsProps={{
              text: 'Apply Previous Course Section Rules',
              buttonType: ButtonType.Ghost,
              onClick: handleLatestSurveyRulesClicked,
              type: 'button',
              disabled: isLatestDataFetching || !latestSyncRules || isButtonDisabled
            }}
          />
        </form>
      </FormProvider>
    </>
  )
}

export default SyncRulesPanel
